import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import {
  CASE_KEY,
  gratingTypeSaved,
  ProblemError,
  setGratingType,
  useCasePlan,
  useLatestCalculation,
  type CaseDetailOut,
  type ClueOut,
  type GratingTypeWord,
} from "@/api";
import { Button } from "@/components/Button";
import { asksBeforeTypeChange, GratingTypeQuestion } from "@/components/GratingTypeQuestion";
import { SelectField } from "@/components/fields/SelectField";
import { StateMessage } from "@/components/StateMessage";
import { SOURCE_WORDS } from "@/features/customer/clues";
import { FACADE_CANCELLED, FACADE_SENT, facadeEditability } from "@/lib/facadeEditability";
import {
  gratingTypeLabel,
  gratingTypeOptions,
  gratingTypeRefusal,
  handSetSentence,
  isGratingTypeWord,
  laidGratingsOf,
  type LaidGratings,
} from "@/lib/gratingTypes";
import { coatingSentence, WELDED_NOT_PRICED } from "@/lib/mailRequest";

import styles from "./Eingang.module.css";

/** What a Vorgang created before §15.14 filled the default shows. */
const NOT_SET = { value: "", label: "nicht gesetzt" };

/**
 * A Stand whose board is not read yet (the plan answer is on its way): the
 * question is asked all the same — a change writes a new Stand either way —
 * and claims nothing about gratings it has not seen.
 */
const NOT_READ_YET: LaidGratings = { following: false, ownTyped: false, handSetConsoles: 0, typedBlanks: 0 };

interface GratingTypeFieldProps {
  caseId: string;
  /** The Vorgang as the Eingang loaded it — its type, its status, and whether it has a Stand. */
  detail: Pick<CaseDetailOut, "gratingType" | "status" | "latestVersionNo" | "mailRequest">;
  /** The type the mail names (`fields.gratingType`), or `null` where it names none or more than one (R-6). */
  clue: ClueOut | null;
}

/**
 * „Rosttyp“ at the Eingang (spec 2026-10-23 R-7): the Vorgang's type, a
 * choice of all three, and — where the mail names another — „Die Mail nennt
 * Barfußrost“ with the one press that takes it.
 *
 * **A proposal, not an assignment** (R-6, A-014): the Vorgang was created
 * with the standard, and the mail's word changes nothing until it is taken.
 * Where it says the type the Vorgang already has, it still stands there —
 * with where it was read, and nothing to press.
 *
 * Every change is `PUT …/grating-type`. It asks first wherever the planning
 * head would (Ruling G-2, one question on both screens): on a released
 * Vorgang (R-4) and on one whose Stand has gratings laid — what happens to
 * their width, and what a person set by hand stays (Ruling G-1). For that
 * the Eingang reads the Stand's board (`GET …/plan`, asked again on every
 * arrival); without a Stand, or with nothing laid, the change goes straight
 * through. Once the offer is out it is a reading. A refusal — a Stand without a
 * measure (422), a Vorgang cancelled meanwhile (409) — keeps the type it had
 * and says the server's sentence.
 */
export function GratingTypeField({ caseId, detail, clue }: GratingTypeFieldProps) {
  const id = useId();
  const client = useQueryClient();
  const selectId = `${id}-select`;
  const clueId = `${id}-clue`;
  const editability = facadeEditability(detail.status);
  const frozen = editability === "sent" || editability === "cancelled";
  const latest = useLatestCalculation(editability === "approved" ? caseId : null);
  // The Stand's board, for the question (Ruling G-2) — asked only where there
  // is one, and read again on every arrival: the board saves without touching
  // this cache, so an answer from before this mount may not know the gratings
  // laid since. Until the fresh one is in, the question claims nothing — nor
  // after the read failed: React Query counts a failed fetch as fetched and
  // keeps the old answer beside the error (review 23.10.2026).
  const hasStand = (detail.latestVersionNo ?? 0) > 0;
  const plan = useCasePlan(hasStand && !frozen ? caseId : null, { fresh: true });
  const planningState = plan.data?.planningState ?? null;
  const laid: LaidGratings | null = !hasStand
    ? null
    : plan.data !== undefined && plan.isFetchedAfterMount && !plan.isError
      ? laidGratingsOf(planningState?.placements ?? [], planningState?.consoleOverrides)
      : NOT_READ_YET;
  // Written here, before the Vorgang is read again: shown while the cache still says the old one.
  const [saved, setSaved] = useState<{ from: string | null; to: string } | null>(null);
  const [pending, setPending] = useState<GratingTypeWord | null>(null);
  const [asking, setAsking] = useState<GratingTypeWord | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  // „übernehmen“ takes its own button away once it went through: the focus
  // goes to the choice, once the render that unlocks it stands. Refused, the
  // button stays, and so does the focus.
  const focusSelect = useRef(false);
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    if (focusTick > 0) document.getElementById(selectId)?.focus();
  }, [focusTick, selectId]);

  const stored = detail.gratingType ?? null;
  const current = saved !== null && saved.from === stored ? saved.to : stored;
  const shown = pending ?? current ?? "";
  const proposed = clue !== null && isGratingTypeWord(clue.value) ? clue.value : null;
  const options = current === null && pending === null ? [NOT_SET, ...gratingTypeOptions()] : gratingTypeOptions();

  async function write(next: GratingTypeWord) {
    setPending(next);
    setRefused(null);
    setAnnouncement("");
    try {
      const answer = await setGratingType(caseId, next);
      setSaved({ from: stored, to: answer.gratingType ?? next });
      // What was set by hand stays — said again once it went through (Ruling G-1).
      const handSet = laid !== null ? handSetSentence(laid) : "";
      setAnnouncement(
        [`Rosttyp: ${gratingTypeLabel(answer.gratingType ?? next)}.`, handSet].filter((part) => part !== "").join(" "),
      );
      void gratingTypeSaved(client, caseId, answer);
      if (focusSelect.current) setFocusTick((tick) => tick + 1);
    } catch (cause) {
      setRefused(gratingTypeRefusal(cause));
      // Cancelled or sent meanwhile (409, `case_cancelled` among them): the
      // Vorgang is read again, and the field gives way to what it is now.
      if (cause instanceof ProblemError && cause.status === 409) {
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId), exact: true });
      }
    } finally {
      setPending(null);
      focusSelect.current = false;
    }
  }

  function choose(value: string, fromButton: boolean) {
    if (pending !== null || frozen || !isGratingTypeWord(value) || value === current) return;
    focusSelect.current = fromButton;
    if (asksBeforeTypeChange(editability === "approved", laid)) {
      setAsking(value);
      return;
    }
    void write(value);
  }

  const review = latest.data?.review ?? null;
  const release = review !== null && review.kind !== "withdrawn" ? { by: review.by, at: review.at } : null;
  const offer = proposed !== null && proposed !== current && !frozen;
  const request = frozen ? null : (detail.mailRequest ?? null);
  const coating = request !== null ? coatingSentence(request) : null;
  const mailNotes = [request?.weldedNamed ? WELDED_NOT_PRICED : null, coating].filter(
    (text): text is string => text !== null,
  );

  return (
    <section className={styles.panel} aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className={styles.panelTitle}>
        {/* The mesh marker before a panel's title, as `Panel` (design 2026-10-24 §8.6). */}
        <span className="marke" aria-hidden="true" />
        Rosttyp
      </h2>
      <div className={styles.typeField}>
        <SelectField
          id={selectId}
          label="Rosttyp"
          labelHidden
          value={shown}
          options={options}
          // Not `disabled` while a change is out: a select disabled under
          // the focus drops it to the page. It shows the choice on its way,
          // and takes no other until that one is answered (`choose`).
          readOnly={frozen}
          hint={editability === "sent" ? FACADE_SENT : editability === "cancelled" ? FACADE_CANCELLED : null}
          onChange={(value) => choose(value, false)}
        />
      </div>
      {proposed !== null ? (
        <div className={styles.typeClue}>
          <p id={clueId} className={styles.typeClueText}>
            <span>Die Mail nennt {gratingTypeLabel(proposed)}</span>{" "}
            <span className={styles.from}>· {SOURCE_WORDS[clue!.source]}</span>
          </p>
          {offer ? (
            <Button
              aria-describedby={clueId}
              busy={pending === proposed}
              aria-disabled={pending !== null ? "true" : undefined}
              onClick={() => choose(proposed, true)}
            >
              {pending === proposed ? "Wird übernommen …" : `${gratingTypeLabel(proposed)} übernehmen`}
            </Button>
          ) : null}
        </div>
      ) : null}
      {/* What the mails ask for that the Werkbank does not price, in the
          planning's words (Task 15b/15d): a welded grating, a coating. Here
          and not only in the customer block, which is closed once the
          customer stands (Drittgutachten). Quiet, as there. */}
      {mailNotes.map((text) => (
        <p key={text} className={styles.typeClueText}>
          {text}
        </p>
      ))}
      {refused !== null ? (
        // One form on both screens: „Rosttyp nicht geändert — {server sentence}“.
        <StateMessage tone="conflict" live="alert" title={refused} />
      ) : null}
      <p role="status" className="visually-hidden">
        {announcement}
      </p>
      {asking !== null ? (
        <GratingTypeQuestion
          from={current}
          to={asking}
          specs={plan.data?.gratingSpecs ?? []}
          approved={editability === "approved"}
          approval={release}
          laid={laid}
          onCancel={() => {
            focusSelect.current = false;
            setAsking(null);
          }}
          onConfirm={() => {
            const next = asking;
            setAsking(null);
            void write(next);
          }}
        />
      ) : null}
    </section>
  );
}
